import {
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRequisitosMinimos } from "../hooks/useRequisitosMinimos";

const rotaDaPendencia = (texto = "") => {
  const valor = texto.toLowerCase();

  if (valor.includes("whatsapp")) return "/configuracoes/whatsapp";
  if (valor.includes("forma de pagamento")) return "/financas/formapagamento";
  if (valor.includes("especialidade")) return "/adm/especialidades";
  if (valor.includes("turno de trabalho")) return "/adm/turnotrabalho";
  if (valor.includes("usuário do sistema")) return "/adm/controleusuarios";
  if (valor.includes("profissional de saúde")) return "/adm/prossaudes";

  return "/home";
};

export default function ConfiguracaoInicialWidget() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isClinic, data, isLoading } = useRequisitosMinimos();

  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  useEffect(() => {
    const abrirConfiguracaoInicial = () => onOpen();
    window.addEventListener("configuracao-inicial:open", abrirConfiguracaoInicial);

    return () => {
      window.removeEventListener("configuracao-inicial:open", abrirConfiguracaoInicial);
    };
  }, [onOpen]);

  if (!isClinic) return null;

  const aprovados = data?.aprovados || [];
  const pendencias = data?.pendencias || [];
  const total = aprovados.length + pendencias.length;
  const percentual = total > 0 ? Math.round((aprovados.length / total) * 100) : 0;

  const irPara = (rota) => {
    onClose();
    window.setTimeout(() => navigate(rota), 250);
  };

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay bg="blackAlpha.500" />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Text>Configuração inicial</Text>
            <Flex align="center" gap={3} mt={3}>
              <CircularProgress
                value={percentual}
                color={percentual === 100 ? "green.400" : "teal.400"}
                size="54px"
              >
                <CircularProgressLabel fontSize="xs" fontWeight="bold">
                  {percentual}%
                </CircularProgressLabel>
              </CircularProgress>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  {aprovados.length} de {total} requisitos concluídos
                </Text>
                <Badge colorScheme={percentual === 100 ? "green" : "orange"} mt={1}>
                  {percentual === 100 ? "Configuração concluída" : `${pendencias.length} pendência(s)`}
                </Badge>
              </Box>
            </Flex>
          </DrawerHeader>

          <DrawerBody py={5}>
            {isLoading ? (
              <Flex justify="center" py={10}><Spinner color="teal.500" /></Flex>
            ) : (
              <VStack align="stretch" spacing={3}>
                {pendencias.map((pendencia) => (
                  <Box key={pendencia} p={4} borderWidth="1px" borderColor="orange.200" borderRadius="md" bg="orange.50">
                    <Flex gap={3} align="flex-start">
                      <Icon as={WarningTwoIcon} color="orange.500" mt="3px" />
                      <Box flex="1">
                        <Text fontSize="sm" color="gray.700">{pendencia}</Text>
                        <Button
                          mt={3}
                          size="sm"
                          colorScheme="teal"
                          variant="outline"
                          onClick={() => irPara(rotaDaPendencia(pendencia))}
                        >
                          Configurar
                        </Button>
                      </Box>
                    </Flex>
                  </Box>
                ))}

                {pendencias.length > 0 && aprovados.length > 0 && <Divider />}

                {aprovados.map((aprovado) => (
                  <Flex key={aprovado} gap={3} align="flex-start" p={3} borderRadius="md" bg="green.50">
                    <Icon as={CheckIcon} color="green.500" mt="3px" />
                    <Text fontSize="sm" color="gray.700">{aprovado}</Text>
                  </Flex>
                ))}

                <Button colorScheme="teal" mt={3} onClick={() => irPara("/home")}>
                  Ver configuração completa
                </Button>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
